import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faLock, faArrowRight, faBolt, faChartLine, faRobot } from '@fortawesome/free-solid-svg-icons';
import { FaTiktok, FaFacebookF, FaLinkedinIn, FaGoogle, FaStar } from 'react-icons/fa';
import logo from 'assets/images/logo.png';
import { postRequest, getRequest } from '../../services/api/apiService';
import { setAuthToken } from 'utils/auth';
import { useLang } from '../../i18n/LanguageContext';
import LanguageSwitcher from '../../i18n/LanguageSwitcher';

// ── Compact product preview ────────────────────────────────────────
function ProductPreview() {
  return (
    <div style={{ background: 'rgba(13,11,30,0.95)', borderRadius: 16, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 24px 60px rgba(34,28,81,0.2)' }}>
      {/* Title bar */}
      <div style={{ padding: '10px 14px', background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', gap: 6 }}>
        <div style={{ width: 9, height: 9, borderRadius: '50%', background: '#ff5f57' }} />
        <div style={{ width: 9, height: 9, borderRadius: '50%', background: '#febc2e' }} />
        <div style={{ width: 9, height: 9, borderRadius: '50%', background: '#28c840' }} />
        <span style={{ marginLeft: 8, fontSize: 10, color: 'rgba(255,255,255,0.3)', fontWeight: 600 }}>Campaign Manager · OneBotAds</span>
        <div style={{ marginLeft: 'auto', width: 6, height: 6, borderRadius: '50%', background: '#34d399', boxShadow: '0 0 6px #34d399' }} />
      </div>
      {/* Stats row */}
      <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        {[{ l: 'Total Spend', v: '$1,204', c: '#a78bfa' }, { l: 'Impressions', v: '781K', c: '#60a5fa' }, { l: 'Avg CTR', v: '3.5%', c: '#34d399' }].map((s, i) => (
          <div key={i} style={{ flex: 1, padding: '10px 12px', borderRight: i < 2 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
            <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 3 }}>{s.l}</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: s.c }}>{s.v}</div>
          </div>
        ))}
      </div>
      {/* Campaign rows */}
      {[
        { name: 'Summer Sale — App Installs', plat: 'TikTok', status: 'ACTIVE', spend: '$892', trend: '+12%', active: true },
        { name: 'Lead Gen — Morocco Q2', plat: 'Meta', status: 'ACTIVE', spend: '$312', trend: '+7%', active: false },
        { name: 'B2B Decision Makers', plat: 'LinkedIn', status: 'PAUSED', spend: '$0', trend: '—', active: false },
      ].map((c, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: c.active ? 'rgba(34,28,81,0.35)' : 'transparent', borderLeft: c.active ? '2px solid #7c3aed' : '2px solid transparent' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: c.active ? '#fff' : 'rgba(255,255,255,0.55)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</div>
            <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.25)', marginTop: 1 }}>{c.plat}</div>
          </div>
          <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 4, fontWeight: 700, background: c.status === 'ACTIVE' ? 'rgba(52,211,153,0.15)' : 'rgba(251,191,36,0.12)', color: c.status === 'ACTIVE' ? '#34d399' : '#fbbf24', flexShrink: 0 }}>{c.status}</span>
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#fff' }}>{c.spend}</div>
            <div style={{ fontSize: 9, color: c.trend !== '—' ? '#34d399' : 'rgba(255,255,255,0.25)' }}>{c.trend}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

const FEATURES = [
  { icon: faBolt, label: 'Launch on 5 platforms at once', color: '#221C51', bg: '#EEF2FF' },
  { icon: faRobot, label: 'AI writes your ad copy instantly', color: '#1877f2', bg: '#EEF4FF' },
  { icon: faChartLine, label: 'Live analytics across all accounts', color: '#059669', bg: '#ECFDF5' },
];

const Login = () => {
  const { t } = useLang();
  const [email, setEmail]           = useState('');
  const [password, setPassword]     = useState('');
  const [errors, setErrors]         = useState({ email: false, password: false });
  const [loginError, setLoginError] = useState('');
  const navigate = useNavigate();

  // Routage post-login (déroulé produit) :
  //   première connexion (aucune régie ni boutique reliée) → Plateformes
  //   connectées, pour brancher son premier compte ;
  //   sinon → Overview (statistiques) — la visite guidée des fonctionnalités
  //   s'y lance automatiquement au premier passage (relançable via "?").
  const routeAfterLogin = async () => {
    try {
      const j = await getRequest('/api/onboarding/journey');
      const connected = (j?.platformsConnected || 0) > 0 || j?.shopifyConnected;

      // Marchand Shopify (users.signup_source figé à l'inscription) qui n'a pas
      // encore relié sa boutique : son premier geste utile est de la connecter,
      // pas de choisir parmi six régies publicitaires. On l'envoie directement
      // sur sa boutique — le reste du parcours en découle.
      if (j?.signupSource === 'shopify' && !j?.shopifyConnected) {
        navigate('/dashboard/stores');
        return;
      }

      // Une page a pu demander a etre rejointe apres le login (ex. /claude,
      // qui envoie s identifier pour connecter ses plateformes).
      let retour = null;
      try { retour = sessionStorage.getItem('postLoginRedirect'); sessionStorage.removeItem('postLoginRedirect'); } catch { /* ignore */ }
      if (retour && retour.startsWith('/')) { navigate(retour); return; }

      navigate(connected ? '/dashboard/statistics' : '/dashboard/connected-platforms');
    } catch {
      navigate('/dashboard/statistics');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = { email: !email, password: !password };
    setErrors(newErrors);
    if (newErrors.email || newErrors.password) return;
    try {
      const response = await postRequest('/api/auth/login', { email, password });
      if (response?.token) { setAuthToken(response.token); await routeAfterLogin(); }
    } catch (error) {
      setLoginError(error.response?.data?.message || 'Invalid email or password.');
    }
  };

  const handleGoogleLoginSuccess = async (response) => {
    try {
      const authResponse = await postRequest('/api/auth/google-login', { token: response.credential });
      if (authResponse?.authToken) { setAuthToken(authResponse.authToken); await routeAfterLogin(); }
    } catch (error) {
      setLoginError(error.response?.data?.message || 'Google login failed.');
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex' }}>
      <style>{`
        @keyframes authFloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
        .auth-input {
          width: 100%; padding: 12px 14px 12px 42px;
          border: 1.5px solid #E5E7EB; border-radius: 10px;
          font-size: 14px; color: #0F0B2D; background: #F9FAFB;
          outline: none; box-sizing: border-box;
          transition: border 0.2s, box-shadow 0.2s, background 0.2s;
        }
        .auth-input::placeholder { color: #9CA3AF; }
        .auth-input:focus { border-color: #221C51; background: #fff; box-shadow: 0 0 0 3px rgba(34,28,81,0.09); }
        .auth-input.error { border-color: #EF4444; background: #fff; }
        .auth-submit-btn {
          width: 100%; padding: 13px; border-radius: 10px;
          background: #221C51; color: #fff; font-size: 15px; font-weight: 700;
          border: none; cursor: pointer; transition: all 0.2s ease;
          display: flex; align-items: center; justify-content: center; gap: 8px;
        }
        .auth-submit-btn:hover { background: #2D2570; transform: translateY(-1px); box-shadow: 0 8px 24px rgba(34,28,81,0.25); }
        @media (max-width: 900px) { .auth-left-panel { display: none !important; } }
      `}</style>

      {/* ── LEFT BRAND PANEL ── */}
      <div className="auth-left-panel" style={{
        width: '52%',
        background: 'linear-gradient(145deg, #F0F4FF 0%, #EEF2FF 50%, #F5F3FF 100%)',
        borderRight: '1px solid #E0E4F0',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: '40px 52px 36px',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Decorative circles */}
        <div style={{ position: 'absolute', top: -120, right: -80, width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(34,28,81,0.06) 0%, transparent 65%)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: -100, left: -80, width: 360, height: 360, borderRadius: '50%', background: 'radial-gradient(circle, rgba(79,70,229,0.05) 0%, transparent 65%)', pointerEvents: 'none' }} />

        {/* Top: Logo + headline */}
        <div style={{ position: 'relative', zIndex: 1 }}>
          <Link to="/">
            <img src={logo} alt="OneBotAds" style={{ height: 32, marginBottom: 36, display: 'block' }} />
          </Link>
          <h2 style={{ fontSize: 'clamp(20px,2.2vw,28px)', fontWeight: 900, color: '#0F0B2D', lineHeight: 1.3, marginBottom: 10, letterSpacing: '-0.02em' }}>
            Your entire ad stack,<br />managed in one place.
          </h2>
          <p style={{ fontSize: 14, color: '#6B7280', lineHeight: 1.65, marginBottom: 28, maxWidth: 360 }}>
            TikTok, Meta, LinkedIn, Snapchat & Google — launch cross-platform campaigns in minutes with AI.
          </p>

          {/* Feature pills */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 28 }}>
            {FEATURES.map((f, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 12, background: '#fff', border: '1px solid #E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                <div style={{ width: 32, height: 32, borderRadius: 9, background: f.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <FontAwesomeIcon icon={f.icon} style={{ fontSize: 13, color: f.color }} />
                </div>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{f.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Middle: Product mockup */}
        <div style={{ position: 'relative', zIndex: 1, animation: 'authFloat 6s ease-in-out infinite', marginBottom: 28 }}>
          <ProductPreview />
        </div>

        {/* Bottom: social proof */}
        <div style={{ position: 'relative', zIndex: 1 }}>
          {/* Platform icons */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 18, alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 600, marginRight: 4 }}>Works with</span>
            {[
              { icon: <FaTiktok />, color: '#010101', bg: '#F3F4F6' },
              { icon: <FaFacebookF />, color: '#1877f2', bg: '#EEF4FF' },
              { icon: <FaLinkedinIn />, color: '#0a66c2', bg: '#EFF6FF' },
              { icon: <FaGoogle />, color: '#4285f4', bg: '#EFF6FF' },
              { icon: '👻', color: '#8B7500', bg: '#FEFCE8' },
            ].map((p, i) => (
              <div key={i} style={{ width: 30, height: 30, borderRadius: 8, background: p.bg, border: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: p.color }}>
                {p.icon}
              </div>
            ))}
          </div>

          {/* Testimonial */}
          <div style={{ padding: '14px 16px', background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div style={{ display: 'flex', gap: 2, marginBottom: 8 }}>
              {[1,2,3,4,5].map(i => <FaStar key={i} style={{ fontSize: 10, color: '#F59E0B' }} />)}
            </div>
            <p style={{ fontSize: 12.5, color: '#374151', lineHeight: 1.6, marginBottom: 10 }}>
              "ONEBOTADS took us from 4 hours per campaign down to under 30 minutes. Incredible."
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#EEF2FF', border: '1.5px solid #C7D2FE', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, color: '#221C51' }}>MR</div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#0F0B2D' }}>Marcus Rivera</div>
                <div style={{ fontSize: 11, color: '#9CA3AF' }}>Head of Digital · Nexus eCommerce</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── RIGHT FORM PANEL ── */}
      <div style={{
        flex: 1,
        background: '#fff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 32px',
      }}>
        {/* Mobile logo */}
        <Link to="/" style={{ display: 'none', marginBottom: 28 }} className="mobile-logo-auth">
          <img src={logo} alt="OneBotAds" style={{ height: 32 }} />
        </Link>
        <style>{`@media(max-width:900px){.mobile-logo-auth{display:flex!important;}}`}</style>

        <div style={{ width: '100%', maxWidth: 380 }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            <LanguageSwitcher />
          </div>
          <div style={{ marginBottom: 32 }}>
            <h1 style={{ fontSize: 24, fontWeight: 800, color: '#0F0B2D', marginBottom: 6, letterSpacing: '-0.02em' }}>{t('Welcome back')}</h1>
            <p style={{ fontSize: 14, color: '#6B7280' }}>
              {t("Don't have an account?")}{' '}
              <Link to="/signup" style={{ color: '#221C51', fontWeight: 600, textDecoration: 'none' }}>{t('Sign up free')}</Link>
            </p>
          </div>

          {loginError && (
            <div style={{ padding: '11px 14px', borderRadius: 10, background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626', fontSize: 13, marginBottom: 20 }}>
              {loginError}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 7 }}>{t('Email address')}</label>
              <div style={{ position: 'relative' }}>
                <FontAwesomeIcon icon={faEnvelope} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF', fontSize: 13, pointerEvents: 'none' }} />
                <input type="email" value={email} onChange={e => { setEmail(e.target.value); setErrors(v => ({ ...v, email: false })); setLoginError(''); }} placeholder="you@example.com" className={`auth-input${errors.email ? ' error' : ''}`} />
              </div>
              {errors.email && <p style={{ fontSize: 12, color: '#EF4444', marginTop: 5 }}>Email is required</p>}
            </div>

            <div style={{ marginBottom: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 7 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{t('Password')}</label>
                <Link to="/forgot-password" style={{ fontSize: 12, color: '#221C51', fontWeight: 500, textDecoration: 'none' }}>{t('Forgot password?')}</Link>
              </div>
              <div style={{ position: 'relative' }}>
                <FontAwesomeIcon icon={faLock} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF', fontSize: 13, pointerEvents: 'none' }} />
                <input type="password" value={password} onChange={e => { setPassword(e.target.value); setErrors(v => ({ ...v, password: false })); setLoginError(''); }} placeholder="••••••••" className={`auth-input${errors.password ? ' error' : ''}`} autoComplete="current-password" />
              </div>
              {errors.password && <p style={{ fontSize: 12, color: '#EF4444', marginTop: 5 }}>Password is required</p>}
            </div>

            <div style={{ marginBottom: 22 }} />

            <button type="submit" className="auth-submit-btn">
              {t('Log in')} <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: 12 }} />
            </button>
          </form>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '22px 0' }}>
            <div style={{ flex: 1, height: 1, background: '#E5E7EB' }} />
            <span style={{ fontSize: 12, color: '#9CA3AF', fontWeight: 500 }}>or</span>
            <div style={{ flex: 1, height: 1, background: '#E5E7EB' }} />
          </div>

          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <GoogleLogin onSuccess={handleGoogleLoginSuccess} onError={() => setLoginError('Google login failed. Please try again.')} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
